<script setup lang="ts">
import { computed, ref } from 'vue';
import { useScroll } from '@vueuse/core';

import {
  useStatsigEventsStore,
  useRouterStore,
  useBrowserStore,
} from '@apps/main/new-desktop/core/stores';
import { RecipeStateKey } from '@apps/main/modules/state/state';

import type { AppMaskType } from '@libs/ui';
import { AppButton, AppMask, AppSvgIcon, AppTooltip } from '@libs/ui';
import { injectStrict } from '@libs/utils';

import type { HomeFlightData } from '../../../../modules/airportSelector';
import { HomeAirportSelectorButton } from '../../../../modules/airportSelector';
import { PaxSelectorButton } from '../../../../modules/paxSelector';

interface TripOverviewHeaderActionsProps {
  paxesButtonText: string;
  homeFlightData: HomeFlightData | null;
  tripStartDate: Date | undefined;
  numberOfDestinations: number;
  priceCalendarButtonText: string;
  numberOfNights: number;
}

const props = defineProps<TripOverviewHeaderActionsProps>();

interface TripOverviewHeaderActionsEmits {
  (eventName: 'pax-click'): void;
  (eventName: 'edit-trip'): void;
  (eventName: 'map-click'): void;
  (eventName: 'home-airport-click'): void;
  (eventName: 'months-selector-click'): void;
}

const emit = defineEmits<TripOverviewHeaderActionsEmits>();

const statsigEventsStore = useStatsigEventsStore();
const routerStore = useRouterStore();
const browserStore = useBrowserStore();
const recipeState = injectStrict(RecipeStateKey);

const actionsRef = ref();

const { arrivedState } = useScroll(actionsRef, {
  behavior: 'smooth',
});

const maskType = computed<AppMaskType>(() => {
  if (arrivedState.left && arrivedState.right) {
    return 'none';
  }
  if (arrivedState.left) {
    return 'right';
  }
  if (arrivedState.right) {
    return 'left';
  }
  return 'both';
});

const isCarTripPlannerUser =
  browserStore.getSessionStorageItem('isCarTripPlannerUser') &&
  browserStore.isLargerThanSmallMax;

const isCarTripPlannerButtonDisabled = computed<boolean>(
  () =>
    recipeState.ferryOnlyRecipe.value.length === 0 &&
    recipeState.flightOnlyRecipe.value.length === 0
);

function openEditTripModal(actionType: 'editTrip' | 'editStay'): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType,
  });

  emit('edit-trip');
}

function openHomeAirportModal(): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType: 'homeAirport',
  });

  emit('home-airport-click');
}

function openPaxSelectorModal(): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType: 'paxSelector',
  });

  emit('pax-click');
}

function openMonthsSelectorModal(): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType: 'monthsSelector',
  });

  emit('months-selector-click');
}

function openFullScreenMap(): void {
  emit('map-click');
}
</script>

<template>
  <AppMask :type="maskType">
    <div ref="actionsRef" class="trip-overview-header-actions">
      <div class="trip-overview-header-actions-wrapper">
        <div v-if="!!isCarTripPlannerUser">
          <AppTooltip
            v-if="!isCarTripPlannerButtonDisabled"
            position="bottom-right"
          >
            <template #trigger>
              <AppButton
                class="car-trip-button"
                type="primary"
                state="disabled"
                size="small"
                :icon="{
                  size: '18',
                  name: 'route',
                  position: 'right',
                }"
                @click="routerStore.navigateToCarTrip()"
              >
                <div class="button-content">
                  <span>
                    {{ $t('open_route_planner') }}
                  </span>
                </div>
              </AppButton>
            </template>
            <template #default>
              {{ $t('planner.car_trip.disabled_button') }}
            </template>
          </AppTooltip>
          <AppButton
            v-else
            class="car-trip-button"
            type="primary"
            state="enabled"
            size="small"
            :icon="{
              size: '18',
              name: 'route',
              position: 'right',
            }"
            @click="routerStore.navigateToCarTrip()"
          >
            <div class="button-content">
              <span>
                {{ $t('open_route_planner') }}
              </span>
            </div>
          </AppButton>
        </div>

        <div v-if="!!isCarTripPlannerUser" class="separator" />

        <AppButton
          class="months-selector-modal-button"
          type="secondary"
          state="enabled"
          size="small"
          :icon="{
            size: '18',
            name: 'calendarOutlined',
            position: 'right',
          }"
          @click="openMonthsSelectorModal()"
        >
          <div class="button-content">
            {{ props.priceCalendarButtonText }}
            <AppSvgIcon name="chevronDown" size="18" />
          </div>
        </AppButton>

        <PaxSelectorButton
          :button-text="props.paxesButtonText"
          @click="openPaxSelectorModal"
        />

        <AppButton
          class="edit-trip-button"
          type="secondary"
          state="enabled"
          size="small"
          :icon="{
            size: '18',
            name: 'moon',
            position: 'right',
          }"
          @click="openEditTripModal('editStay')"
        >
          <div class="button-content">
            <span>
              {{ props.numberOfNights }}
              {{ $t('planner.nights', props.numberOfNights).toLowerCase() }}
            </span>
            <AppSvgIcon name="chevronDown" size="18" />
          </div>
        </AppButton>

        <AppButton
          class="edit-trip-button"
          type="secondary"
          state="enabled"
          size="small"
          :icon="{
            size: '18',
            name: 'locationMarkOutlined',
            position: 'right',
          }"
          @click="openEditTripModal('editTrip')"
        >
          <div class="button-content">
            <span>
              {{ props.numberOfDestinations }}
              {{
                $t(
                  'planner.destination',
                  props.numberOfDestinations
                ).toLowerCase()
              }}
            </span>
            <AppSvgIcon name="chevronDown" size="18" />
          </div>
        </AppButton>

        <HomeAirportSelectorButton
          v-if="props.homeFlightData"
          :home-flight-data="props.homeFlightData"
          @click="openHomeAirportModal"
        />
      </div>
      <div
        v-if="
          browserStore.isLargerThanSmallMin &&
          browserStore.isSmallerThanLargeMin
        "
        class="trip-overview-header-actions-wrapper"
      >
        <AppButton
          class="map-button"
          type="secondary"
          state="enabled"
          size="small"
          :icon="{
            size: '18',
            name: 'map',
            position: 'right',
          }"
          @click="openFullScreenMap"
        >
          <div class="button-content">
            <span>{{ $t('product_search_selector.map') }}</span>
          </div>
        </AppButton>
      </div>
    </div>
  </AppMask>
</template>

<style scoped lang="scss">
.trip-overview-header-actions {
  display: flex;
  overscroll-behavior-x: contain; // NOTE: this prevents going back/forward in the browser navigation when reaching the scroll limit
  overflow-y: hidden;
  align-items: space-between;
  justify-content: space-between;
  gap: var(--spacing-8);
  padding-bottom: var(--spacing-16);

  .trip-overview-header-actions-wrapper {
    display: flex;
    gap: var(--spacing-8);
    // NOTE: To correctly display button focused state
    padding: var(--spacing-2);
    .separator {
      width: 1px;
      background-color: var(--color-scrollbar);
      margin: 0 var(--spacing-8);
    }
    .button-content {
      display: flex;
      align-items: center;
      gap: var(--spacing-8);
    }
    .map-button {
      width: max-content;
    }
  }
}
</style>
